<template>
  <header class="topbar" @keydown.esc="closeMenus">
    <!-- ============ Seletor de conta / workspace ============ -->
    <div class="tb-dd">
      <button class="tb-ws" @click.stop="toggleMenu('ws')">
        <span class="tb-ws-txt">
          <small>Conta</small>
          <b>{{ currentAccountName }}</b>
        </span>
        <svg class="ic ic-sm caret" viewBox="0 0 24 24"><path d="m6 9 6 6 6-6" /></svg>
      </button>

      <div v-if="activeMenu === 'ws'" class="menu menu-accounts">
        <div class="menu-label">Contas</div>
        <label v-if="showAccountSearch" class="acc-search" @click.stop>
          <svg class="ic ic-sm" viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            ref="accSearchInput"
            v-model="accountQuery"
            type="text"
            placeholder="Buscar conta…"
          />
        </label>
        <div class="acc-list" @scroll="onAccScroll">
          <button
            v-for="account in visibleAccounts"
            :key="account.id"
            class="menu-item"
            @click="switchAccount(account.id)"
          >
            <span class="mi-txt">
              <b>{{ account.name }}</b>
              <small>{{ account.role || 'Membro' }}</small>
            </span>
            <svg
              v-if="account.id === accountId"
              class="ic ic-sm"
              style="color: #0a7581"
              viewBox="0 0 24 24"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </button>
          <div v-if="!filteredAccounts.length" class="acc-empty">
            Nenhuma conta encontrada
          </div>
          <div v-else-if="hasMoreAccounts" class="acc-more">
            Role para carregar mais ({{
              filteredAccounts.length - visibleAccounts.length
            }})
          </div>
        </div>
      </div>
    </div>

    <!-- ============ Busca global ============ -->
    <div class="tb-center">
      <label class="tb-search" @click="focusInputSearch">
        <svg class="ic ic-sm" viewBox="0 0 24 24">
          <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
        </svg>
        <input
          ref="searchInput"
          type="text"
          :placeholder="searchPlaceholder"
          :value="searchQuery"
          @input="debounceSearch"
          @focus="handleFocusSearch"
          @blur="handleBlurSearch"
        />
        <span class="tb-kbd"><kbd>⌘</kbd><kbd>K</kbd></span>
      </label>
    </div>

    <!-- ============ Direita: Criar / Ajuda / Notificações / Usuário ============ -->
    <div class="tb-right">
      <!-- + Criar -->
      <div class="tb-dd">
        <button class="tb-create" @click.stop="toggleMenu('create')">
          <svg class="ic ic-sm" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>
          Criar
          <svg class="ic ic-sm" viewBox="0 0 24 24"><path d="m6 9 6 6 6-6" /></svg>
        </button>
        <div v-if="activeMenu === 'create'" class="menu right">
          <div class="menu-label">Atendimento</div>
          <button class="menu-item" @click="createAction('conversation')">
            <span class="mi-ico" style="background: #e1f8fb">
              <svg class="ic ic-sm" style="color: #0a7581" viewBox="0 0 24 24">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />
              </svg>
            </span>
            <span class="mi-txt"><b>Nova conversa</b><small>Iniciar atendimento ativo</small></span>
          </button>
          <button class="menu-item" @click="createAction('contact')">
            <span class="mi-ico" style="background: #e7f6ee">
              <svg class="ic ic-sm" style="color: #1f9d63" viewBox="0 0 24 24">
                <path
                  d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z"
                />
              </svg>
            </span>
            <span class="mi-txt"><b>Novo contato</b><small>Cadastrar cliente/lead</small></span>
          </button>
          <div class="menu-sep" />
          <div class="menu-label">CRM</div>
          <button
            class="menu-item"
            :class="{ locked: !isCrmEnabled }"
            @click="isCrmEnabled && createAction('opportunity')"
          >
            <span class="mi-ico" style="background: #f0ecfe">
              <svg class="ic ic-sm" style="color: #7a5cf0" viewBox="0 0 24 24">
                <path d="M3 3v18h18" /><path d="m7 14 4-4 3 3 5-6" />
              </svg>
            </span>
            <span class="mi-txt"
              ><b>Nova oportunidade</b><small>Adicionar ao funil de vendas</small></span
            >
            <span v-if="!isCrmEnabled" class="mi-lock">Upgrade</span>
          </button>
          <button
            class="menu-item"
            :class="{ locked: !isSurveyEnabled }"
            @click="isSurveyEnabled && createAction('survey')"
          >
            <span class="mi-ico" style="background: #fdf2e2">
              <svg class="ic ic-sm" style="color: #d98014" viewBox="0 0 24 24">
                <path
                  d="M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM23 21v-2a4 4 0 0 0-3-3.9M1 21v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2"
                />
              </svg>
            </span>
            <span class="mi-txt"><b>Nova pesquisa</b><small>NPS / CSAT</small></span>
            <span v-if="!isSurveyEnabled" class="mi-lock">Upgrade</span>
          </button>
          <div class="menu-sep" />
          <button
            class="menu-item"
            :class="{ locked: !isCampaignFlowEnabled }"
            @click="isCampaignFlowEnabled && createAction('campaign')"
          >
            <span
              class="mi-ico"
              :style="{
                background: isCampaignFlowEnabled ? '#fdecec' : '#f0f3f5',
              }"
            >
              <svg
                class="ic ic-sm"
                :style="{ color: isCampaignFlowEnabled ? '#dc4b4b' : '#8a97a3' }"
                viewBox="0 0 24 24"
              >
                <path d="m3 11 18-5v12L3 14v-3Z" />
              </svg>
            </span>
            <span class="mi-txt"
              ><b>Nova campanha</b><small>Fluxo de campanhas</small></span
            >
            <span v-if="!isCampaignFlowEnabled" class="mi-lock">Upgrade</span>
          </button>
        </div>
      </div>

      <div class="tb-divider" />

      <!-- Ajuda -->
      <div class="tb-dd">
        <button class="tb-icon" title="Ajuda" @click.stop="toggleMenu('help')">
          <svg class="ic" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" /><path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3M12 17h.01" />
          </svg>
        </button>
        <div v-if="activeMenu === 'help'" class="menu right">
          <button class="menu-item" @click="helpAction('help')">
            <span class="mi-ico" style="background: #f0f3f5">
              <svg class="ic ic-sm" viewBox="0 0 24 24">
                <path
                  d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2ZM22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7Z"
                />
              </svg>
            </span>
            <span class="mi-txt"><b>Central de ajuda</b></span>
          </button>
          <button class="menu-item" @click="helpAction('support')">
            <span class="mi-ico" style="background: #f0f3f5">
              <svg class="ic ic-sm" viewBox="0 0 24 24">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />
              </svg>
            </span>
            <span class="mi-txt"><b>Falar com o suporte</b></span>
          </button>
          <button class="menu-item" @click="helpAction('videos')">
            <span class="mi-ico" style="background: #f0f3f5">
              <svg class="ic ic-sm" viewBox="0 0 24 24">
                <rect x="2" y="4" width="20" height="16" rx="2" /><path d="M10 9l5 3-5 3V9Z" />
              </svg>
            </span>
            <span class="mi-txt"><b>Tutoriais em vídeo</b></span>
          </button>
          <div class="menu-sep" />
          <button class="menu-item" @click="openShortcuts">
            <span class="mi-ico" style="background: #f0f3f5">
              <svg class="ic ic-sm" viewBox="0 0 24 24">
                <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M7 8h4M7 12h2" />
              </svg>
            </span>
            <span class="mi-txt"><b>Atalhos de teclado</b><small>⌘ /</small></span>
          </button>
        </div>
      </div>

      <!-- Notificações -->
      <button
        class="tb-icon"
        title="Notificações"
        @click.stop="openNotificationPanel"
      >
        <svg class="ic" viewBox="0 0 24 24">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0" />
        </svg>
        <span v-if="unreadCount" class="tb-dot">{{ unreadCount }}</span>
      </button>

      <div class="tb-divider" />

      <!-- Status de disponibilidade -->
      <div class="tb-dd">
        <button
          class="tb-status"
          :class="`st-${currentUserAvailability}`"
          @click.stop="toggleMenu('status')"
        >
          <span class="st-dot" :style="{ background: statusColor }" />
          {{ statusLabel }}
          <svg class="ic ic-sm caret" viewBox="0 0 24 24"><path d="m6 9 6 6 6-6" /></svg>
        </button>
        <div v-if="activeMenu === 'status'" class="menu right menu-status">
          <div class="menu-label">{{ $t('SIDEBAR.SET_AVAILABILITY_TITLE') }}</div>
          <button
            v-for="s in availabilityStatuses"
            :key="s.value"
            class="menu-item status-item"
            @click="changeAvailabilityStatus(s.value)"
          >
            <span class="st-dot" :style="{ background: s.color }" />
            <span class="mi-txt"><b>{{ s.label }}</b></span>
            <svg
              v-if="s.value === currentUserAvailability"
              class="ic ic-sm"
              style="color: #0a7581"
              viewBox="0 0 24 24"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </button>
          <div class="menu-sep" />
          <div class="menu-item auto-offline" @click.stop>
            <svg
              v-tooltip.left="$t('SIDEBAR.SET_AUTO_OFFLINE.INFO_TEXT')"
              class="ic ic-sm ao-info"
              viewBox="0 0 24 24"
            >
              <circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" />
            </svg>
            <span class="mi-txt ao-text">{{
              $t('SIDEBAR.SET_AUTO_OFFLINE.TEXT')
            }}</span>
            <button
              class="ao-switch"
              :class="{ on: currentUserAutoOffline }"
              @click.stop="toggleAutoOffline"
            >
              <span class="ao-knob" />
            </button>
          </div>
        </div>
      </div>

      <!-- Perfil do usuário -->
      <div class="tb-dd">
        <button class="tb-user" @click.stop="toggleMenu('user')">
          <span class="tb-user-av">
            {{ userInitial }}
            <span class="st" :style="{ background: statusColor }" />
          </span>
          <span class="tb-user-meta">
            <b>{{ userFirstName }}</b>
            <small>{{ statusLabel }}</small>
          </span>
          <svg class="ic ic-sm caret-user" viewBox="0 0 24 24"><path d="m6 9 6 6 6-6" /></svg>
        </button>
        <div v-if="activeMenu === 'user'" class="menu right">
          <div class="menu-user-head">
            <b>{{ currentUser.name }}</b>
            <small>{{ currentUser.email }}</small>
          </div>
          <div class="menu-sep" />
          <button class="menu-item" @click="userAction('profile')">
            <span class="mi-ico" style="background: #f0f3f5">
              <svg class="ic ic-sm" viewBox="0 0 24 24">
                <circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" />
              </svg>
            </span>
            <span class="mi-txt"><b>Meu perfil</b></span>
          </button>
          <button class="menu-item" @click="userAction('settings')">
            <span class="mi-ico" style="background: #f0f3f5">
              <svg class="ic ic-sm" viewBox="0 0 24 24">
                <path
                  d="M20 7h-9M14 17H5M17 17a3 3 0 1 0 6 0 3 3 0 0 0-6 0ZM7 7a3 3 0 1 0-6 0 3 3 0 0 0 6 0Z"
                />
              </svg>
            </span>
            <span class="mi-txt"
              ><b>Configurações</b><small>Conta {{ currentAccountName }}</small></span
            >
          </button>
          <div class="menu-sep" />
          <button
            v-if="isSuperAdmin"
            class="menu-item"
            @click="openSuperAdmin"
          >
            <span class="mi-ico" style="background: #f0f3f5">
              <svg class="ic ic-sm" viewBox="0 0 24 24">
                <path
                  d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6l7-3Z"
                />
                <path d="M9.5 12l1.8 1.8L15 10" />
              </svg>
            </span>
            <span class="mi-txt"><b>Console de Super Admin</b></span>
          </button>
          <button class="menu-item" @click="logout">
            <span class="mi-ico" style="background: #fdecec">
              <svg class="ic ic-sm" style="color: #dc4b4b" viewBox="0 0 24 24">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
              </svg>
            </span>
            <span class="mi-txt"><b style="color: #dc4b4b">Sair</b></span>
          </button>
        </div>
      </div>
    </div>

    <woot-modal
      v-if="queryConversation"
      :show.sync="modalFilters"
      :on-close="closeModalFilters"
      size="small"
      custom-class="search-modal"
      @close="closeModalFilters"
    >
      <search-view @closeModal="closeModalFilters" />
    </woot-modal>
  </header>
</template>

<script>
import { mapGetters } from 'vuex';
import { frontendURL } from '../../helper/URLHelper';
import SearchView from '../../modules/search/components/SearchView.vue';
import router from '..';
import { CONVERSATION_EVENTS } from '../../helper/AnalyticsHelper/events';
import { canAccessCRM } from '../../helper/crmPermissionHelper';
import Auth from '../../api/auth';

const STATUS_MAP = {
  online: { label: 'Disponível', color: '#1f9d63' },
  busy: { label: 'Ocupado', color: '#f5a623' },
  offline: { label: 'Desconectado', color: '#7e8ea3' },
};
const AVAILABILITY_KEYS = ['online', 'busy', 'offline'];

// Quantas contas renderizar por vez no seletor (carrega mais ao rolar)
const ACCOUNTS_PAGE_SIZE = 30;
// A partir de quantas contas o campo de busca aparece
const ACCOUNTS_SEARCH_THRESHOLD = 6;

export default {
  name: 'HeaderApp',
  components: {
    SearchView,
  },
  data() {
    return {
      activeMenu: null,
      searchQuery: '',
      isFocused: false,
      pageDefault: true,
      modalFilters: false,
      isNotificationPanel: false,
      accountQuery: '',
      accountsToShow: ACCOUNTS_PAGE_SIZE,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentUser: 'getCurrentUser',
      userAccounts: 'getUserAccounts',
      currentUserAvailability: 'getCurrentUserAvailability',
      notificationMetadata: 'notifications/getMeta',
      queryConversation: 'conversationSearch/getQueryConversation',
      getAccount: 'accounts/getAccount',
      currentUserAutoOffline: 'getCurrentUserAutoOffline',
      kanbanList: 'getKanbanfunnels',
    }),
    account() {
      return this.getAccount(this.accountId);
    },
    availabilityStatuses() {
      const labels = this.$t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUSES_LIST');
      return AVAILABILITY_KEYS.map((value, i) => ({
        value,
        label: labels[i],
        color: STATUS_MAP[value].color,
      }));
    },
    // Módulos ativos na conta — mesma fonte da verdade do menu lateral
    // (booleans da conta, não a jsonb `features`).
    isCrmEnabled() {
      return canAccessCRM(this.account, this.currentUser, this.kanbanList);
    },
    isSurveyEnabled() {
      return !!this.account?.enable_smart_surveys;
    },
    isCampaignFlowEnabled() {
      return !!this.account?.enable_campaign_flow;
    },
    currentAccountName() {
      return (this.account && this.account.name) || 'Conta';
    },
    isSuperAdmin() {
      return this.currentUser?.type === 'SuperAdmin';
    },
    showAccountSearch() {
      return (this.userAccounts || []).length > ACCOUNTS_SEARCH_THRESHOLD;
    },
    filteredAccounts() {
      const accounts = this.userAccounts || [];
      // Divide em termos (busca por várias palavras, ordem indiferente)
      const tokens = this.normalizeText(this.accountQuery)
        .split(/\s+/)
        .filter(Boolean);
      if (!tokens.length) return accounts;
      return accounts.filter(acc => {
        const haystack = `${this.normalizeText(acc.name)} ${acc.id}`;
        return tokens.every(token => haystack.includes(token));
      });
    },
    visibleAccounts() {
      return this.filteredAccounts.slice(0, this.accountsToShow);
    },
    hasMoreAccounts() {
      return this.filteredAccounts.length > this.visibleAccounts.length;
    },
    userFirstName() {
      return (this.currentUser.name || '').split(' ')[0] || 'Usuário';
    },
    userInitial() {
      return (this.currentUser.name || 'U').charAt(0).toUpperCase();
    },
    statusLabel() {
      return (STATUS_MAP[this.currentUserAvailability] || STATUS_MAP.offline).label;
    },
    statusColor() {
      return (STATUS_MAP[this.currentUserAvailability] || STATUS_MAP.offline).color;
    },
    unreadCount() {
      const count = this.notificationMetadata.unreadCount;
      if (!count) return '';
      return count < 100 ? `${count}` : '99+';
    },
    searchPlaceholder() {
      return 'Buscar contatos, conversas, protocolos, oportunidades…';
    },
  },
  watch: {
    queryConversation: {
      handler(newValue) {
        this.searchQuery = newValue;
        if (newValue === 'close-modal') {
          this.closeModalFilters();
        }
      },
      immediate: true,
    },
    accountQuery() {
      // Ao filtrar, volta a mostrar a primeira "página" de resultados
      this.accountsToShow = ACCOUNTS_PAGE_SIZE;
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClick);
  },
  methods: {
    // ----- Dropdowns -----
    toggleMenu(name) {
      this.activeMenu = this.activeMenu === name ? null : name;
      if (this.activeMenu === 'ws') {
        // Abriu o seletor de contas: limpa a busca, reinicia a paginação e foca
        this.accountQuery = '';
        this.accountsToShow = ACCOUNTS_PAGE_SIZE;
        this.$nextTick(() => {
          if (this.$refs.accSearchInput) this.$refs.accSearchInput.focus();
        });
      }
    },
    closeMenus() {
      this.activeMenu = null;
    },
    normalizeText(text) {
      // Minúsculas + remove acentos/diacríticos (á→a, ç→c, í→i, ã→a...)
      return (text || '')
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
    },
    onAccScroll(e) {
      const el = e.target;
      const nearBottom =
        el.scrollTop + el.clientHeight >= el.scrollHeight - 48;
      if (nearBottom && this.hasMoreAccounts) {
        this.accountsToShow += ACCOUNTS_PAGE_SIZE;
      }
    },
    onDocumentClick(e) {
      if (!this.$el.contains(e.target) || !e.target.closest('.tb-dd')) {
        this.closeMenus();
      }
    },
    // ----- Disponibilidade -----
    changeAvailabilityStatus(availability) {
      if (availability !== this.currentUserAvailability) {
        this.$store.dispatch('updateAvailability', {
          availability,
          account_id: this.accountId,
        });
      }
      this.closeMenus();
    },
    toggleAutoOffline() {
      this.$store.dispatch('updateAutoOffline', {
        accountId: this.accountId,
        autoOffline: !this.currentUserAutoOffline,
      });
    },
    // ----- Contas -----
    switchAccount(id) {
      this.closeMenus();
      if (id === this.accountId) return;
      window.location.href = `/app/accounts/${id}/conversation`;
    },
    // ----- Criar -----
    createAction(type) {
      this.closeMenus();
      const base = `accounts/${this.accountId}`;
      const routes = {
        conversation: `${base}/conversation`,
        contact: `${base}/contacts`,
        opportunity: `${base}/crm`,
        survey: `${base}/smart_survey`,
        campaign: `${base}/campaigns_flow`,
      };
      const path = routes[type];
      if (path) router.push({ path: frontendURL(path) });
    },
    // ----- Ajuda -----
    helpAction() {
      this.closeMenus();
    },
    openShortcuts() {
      this.closeMenus();
      this.$emit('open-key-shortcut-modal');
    },
    // ----- Usuário -----
    userAction(type) {
      this.closeMenus();
      const base = `accounts/${this.accountId}`;
      const routes = {
        profile: `${base}/profile/settings`,
        settings: `${base}/settings/general`,
      };
      const path = routes[type];
      if (path) router.push({ path: frontendURL(path) });
    },
    openSuperAdmin() {
      this.closeMenus();
      // Rota servida pelo Rails (fora do SPA); abre em nova aba como no original.
      window.open('/super_admin', '_blank', 'noopener');
    },
    logout() {
      this.closeMenus();
      Auth.logout();
    },
    // ----- Notificações -----
    openNotificationPanel() {
      this.closeMenus();
      this.isNotificationPanel = true;
      if (this.$route.name !== 'notifications_index') {
        this.$emit('open-notification-panel');
      }
    },
    // ----- Busca -----
    handleFocusSearch() {
      this.isFocused = true;
    },
    handleBlurSearch() {
      this.isFocused = false;
    },
    focusInputSearch() {
      if (this.$refs.searchInput) this.$refs.searchInput.focus();
    },
    debounceSearch(e) {
      this.searchQuery = e.target.value;
      this.modalFilters = true;
      this.$store.dispatch('conversationSearch/setQuery', e.target.value);
      clearTimeout(this.debounce);
      this.debounce = setTimeout(async () => {
        if (this.searchQuery.length > 2 || this.searchQuery.match(/^[0-9]+$/)) {
          this.onSearch(this.searchQuery);
        }
      }, 500);
    },
    onSearch(q) {
      this.query = q;
      if (!q) {
        this.$store.dispatch('conversationSearch/clearSearchResults');
        this.closeModalFilters();
        return;
      }
      this.$track(CONVERSATION_EVENTS.SEARCH_CONVERSATION);
      this.$store.dispatch('conversationSearch/fullSearch', { q });
    },
    closeModalFilters() {
      this.$store.dispatch('conversationSearch/setQuery', '');
      this.modalFilters = false;
    },
  },
};
</script>

<style lang="scss" scoped>
// Tokens do protótipo (barra superior clara)
$surface: #ffffff;
$surface-2: #f0f3f5;
$border: #e4e9ee;
$border-strong: #d3dbe2;
$text: #0f1f2a;
$text-2: #516170;
$text-3: #8a97a3;
$brand: #01c6d8;
$brand-050: #e1f8fb;
$brand-600: #03b2c3;
$brand-ink: #002136;
$navy: #002136;

.topbar {
  height: 56px;
  flex: none;
  display: flex;
  align-items: center;
  gap: var(--space-normal);
  background: $surface;
  border-bottom: 1px solid $border;
  padding: 0 var(--space-normal);
  color: $text;
  z-index: 50;
}

.ic {
  width: 20px;
  height: 20px;
  flex: none;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.ic-sm {
  width: 16px;
  height: 16px;
}

// ---------- Seletor de conta ----------
.tb-ws {
  display: flex;
  align-items: center;
  gap: var(--space-one);
  padding: 5px 10px 5px 6px;
  border-radius: 10px;
  transition: background 0.15s ease-in-out;
  flex: none;
  cursor: pointer;

  &:hover {
    background: $surface-2;
  }

  .caret {
    color: $text-3;
  }
}
.tb-ws-txt {
  text-align: left;
  line-height: 1.15;

  small {
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: $text-3;
    display: block;
  }
  b {
    font-size: 13px;
    font-weight: 700;
    color: $text;
  }
}

// ---------- Busca ----------
.tb-center {
  flex: 1;
  display: flex;
  justify-content: center;
  min-width: 0;
}
.tb-search {
  display: flex;
  align-items: center;
  gap: var(--space-one);
  width: 100%;
  max-width: 52rem;
  background: $surface-2;
  border: 1px solid $border;
  border-radius: 10px;
  padding: 0 var(--space-slab);
  height: 38px;
  transition: background 0.15s ease-in-out, border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;
  cursor: text;

  &:hover {
    border-color: $border-strong;
  }
  &:focus-within {
    background: #fff;
    border-color: $brand;
    box-shadow: 0 0 0 3px $brand-050;
  }

  svg {
    color: $text-3;
  }
  input {
    flex: 1;
    min-width: 0;
    border: none;
    background: none;
    outline: none;
    color: $text;
    font-size: 1.35rem;
    box-shadow: none !important;
    margin: 0;
    height: auto;

    &::placeholder {
      color: $text-3;
    }
    &:focus {
      box-shadow: none !important;
    }
  }
  &:focus-within .tb-kbd {
    display: none;
  }
}
.tb-kbd {
  display: flex;
  gap: 3px;
  flex: none;

  kbd {
    font-size: 10px;
    font-weight: 600;
    color: $text-3;
    background: #fff;
    border: 1px solid $border;
    border-radius: 5px;
    padding: 2px 5px;
    line-height: 1;
  }
}

// ---------- Botão Criar ----------
.tb-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: none;
}
.tb-create {
  display: flex;
  align-items: center;
  gap: 7px;
  height: 38px;
  padding: 0 var(--space-slab);
  flex: none;
  background: $brand;
  color: #fff;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease-in-out;

  &:hover {
    background: $brand-600;
  }
}

// ---------- Ícones (ajuda / notificações) ----------
.tb-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  color: $text-2;
  cursor: pointer;
  transition: background 0.15s ease-in-out, color 0.15s ease-in-out;
  position: relative;
  background: transparent;
  border: 0;

  &:hover {
    background: $surface-2;
    color: $text;
  }
}
.tb-dot {
  position: absolute;
  top: 6px;
  right: 7px;
  min-width: 16px;
  height: 16px;
  padding: 0 3px;
  background: $brand;
  color: #fff;
  font-size: 9px;
  font-weight: 800;
  border-radius: 8px;
  display: grid;
  place-items: center;
  border: 2px solid $surface;
}
.tb-divider {
  width: 1px;
  height: 24px;
  background: $border;
  margin: 0 4px;
}

// ---------- Usuário ----------
.tb-user {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 4px 8px 4px 4px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s ease-in-out;

  &:hover {
    background: $surface-2;
  }
  .caret-user {
    color: rgba(15, 31, 42, 0.4);
  }
}
.tb-user-av {
  position: relative;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ffb56b, #f78e3d);
  display: grid;
  place-items: center;
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  flex: none;

  .st {
    position: absolute;
    bottom: -1px;
    right: -1px;
    width: 11px;
    height: 11px;
    border-radius: 50%;
    border: 2px solid $surface;
  }
}
.tb-user-meta {
  text-align: left;
  line-height: 1.2;

  b {
    font-size: 12.5px;
    font-weight: 600;
    color: $text;
    display: block;
  }
  small {
    font-size: 10.5px;
    color: $text-3;
  }
}

// ---------- Dropdown genérico ----------
.tb-dd {
  position: relative;
}
.menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  min-width: 25rem;
  background: #fff;
  border: 1px solid $border;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(16, 32, 45, 0.16);
  padding: 6px;
  z-index: 70;
  color: $text;
  animation: menuIn 0.14s ease;

  &.right {
    left: auto;
    right: 0;
  }
}
@keyframes menuIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
.menu-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: $text-3;
  padding: 8px 10px 4px;
}

// ---------- Seletor de contas: busca + lista rolável ----------
.menu-accounts {
  min-width: 28rem;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 8rem);
}
.acc-search {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 2px 4px 6px;
  padding: 0 10px;
  height: 34px;
  background: $surface-2;
  border: 1px solid $border;
  border-radius: 8px;
  cursor: text;

  &:focus-within {
    background: #fff;
    border-color: $brand;
    box-shadow: 0 0 0 3px $brand-050;
  }
  svg {
    color: $text-3;
    flex: none;
  }
  input {
    flex: 1;
    min-width: 0;
    border: none;
    background: none;
    outline: none;
    color: $text;
    font-size: 1.3rem;
    margin: 0;
    height: auto;
    box-shadow: none !important;

    &::placeholder {
      color: $text-3;
    }
  }
}
.acc-list {
  overflow-y: auto;
  max-height: 34rem;
  margin: 0 -2px;
  padding: 0 2px;
}
.acc-empty {
  padding: 16px 10px;
  text-align: center;
  font-size: 12px;
  color: $text-3;
}
.acc-more {
  padding: 8px 10px 4px;
  text-align: center;
  font-size: 10.5px;
  font-weight: 600;
  color: $text-3;
}
.menu-user-head {
  padding: 8px 10px 4px;

  b {
    font-size: 13px;
    font-weight: 700;
    display: block;
  }
  small {
    font-size: 11px;
    color: $text-3;
  }
}
.menu-item {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 13px;
  color: $text;
  width: 100%;
  text-align: left;
  cursor: pointer;
  background: transparent;
  border: 0;
  transition: background 0.12s ease-in-out;

  &:hover {
    background: $surface-2;
  }
  &.locked {
    opacity: 0.55;
    cursor: default;

    &:hover {
      background: transparent;
    }
  }

  .mi-ico {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    display: grid;
    place-items: center;
    flex: none;

    svg {
      width: 16px;
      height: 16px;
    }
  }
  .mi-txt {
    flex: 1;
    min-width: 0;

    b {
      font-weight: 600;
      display: block;
    }
    small {
      font-size: 11px;
      color: $text-3;
    }
  }
  .mi-lock {
    font-size: 9.5px;
    font-weight: 700;
    color: $text-3;
    background: $surface-2;
    padding: 2px 6px;
    border-radius: 5px;
  }
}
.menu-sep {
  height: 1px;
  background: $border;
  margin: 6px 4px;
}

// ---------- Status de disponibilidade ----------
.tb-status {
  display: flex;
  align-items: center;
  gap: 7px;
  height: 34px;
  padding: 0 12px;
  border-radius: 20px;
  font-size: 12.5px;
  font-weight: 700;
  transition: filter 0.15s;
  flex: none;
}
.tb-status:hover {
  filter: brightness(0.96);
}
.tb-status .caret {
  opacity: 0.65;
}
.tb-status.st-online {
  background: #e7f6ee;
  color: #177a4a;
}
.tb-status.st-busy {
  background: #fdf2e2;
  color: #a8620f;
}
.tb-status.st-offline {
  background: #eef1f4;
  color: #516170;
}
.st-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex: none;
}
.menu-status {
  min-width: 24rem;
}
.status-item .st-dot {
  margin: 0 1px;
}
.auto-offline {
  gap: 8px;
  cursor: default !important;
}
.auto-offline:hover {
  background: transparent !important;
}
.auto-offline .ao-info {
  color: $text-3;
  flex: none;
}
.auto-offline .ao-text {
  flex: 1;
  font-size: 12px;
  font-weight: 500;
  color: $text-2;
}
.ao-switch {
  position: relative;
  width: 36px;
  height: 20px;
  border-radius: 20px;
  background: $border-strong;
  border: 0;
  flex: none;
  cursor: pointer;
  transition: background 0.15s;
  padding: 0;
}
.ao-switch.on {
  background: $brand;
}
.ao-knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 32, 45, 0.25);
  transition: transform 0.15s;
}
.ao-switch.on .ao-knob {
  transform: translateX(16px);
}

.search-modal {
  align-items: flex-start !important;
  margin-top: 56px !important;
}
</style>
